import type { ShipmentTemplateWithTags } from '~api/userprofile';

import { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Button, MoreButton } from '@ingrid/luterkort-react';

import { DetailsDrawer as Drawer } from '~src/components/DetailsDrawer/DetailsDrawer';
import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { AppRoute } from '~src/main/routes';

import { Menu } from './menu';

interface Props {
  shipmentTemplateWithTags?: ShipmentTemplateWithTags;
}

function ShipmentTemplateDrawerAction({ shipmentTemplateWithTags }: Props) {
  const { t } = useTranslation();

  if (!shipmentTemplateWithTags?.template) {
    return false;
  }

  return (
    <Fragment>
      <RoleGuard roles={['transport:edit']} withTooltip>
        <Button as={RouterLink} to={`${AppRoute.EDIT}/${shipmentTemplateWithTags.template?.id}`}>
          {t('common.edit')}
        </Button>
      </RoleGuard>

      <Menu template={shipmentTemplateWithTags.template} trigger={<MoreButton variant="secondary" />} />
    </Fragment>
  );
}

export function DetailsDrawer({ shipmentTemplateWithTags }: Props) {
  return (
    <Drawer
      detailsHref={shipmentTemplateWithTags?.template?.id}
      actions={<ShipmentTemplateDrawerAction shipmentTemplateWithTags={shipmentTemplateWithTags} />}
      nodes={[
        <Drawer.Plain
          label="shipmentTemplates.details.templateName"
          value={shipmentTemplateWithTags?.template?.data?.name}
          key="name"
        />,
        <Drawer.Address
          label="common.shipFrom"
          value={shipmentTemplateWithTags?.template?.data?.address_from}
          key="from"
        />,
        <Drawer.Address label="common.shipTo" value={shipmentTemplateWithTags?.template?.data?.address_to} key="to" />,
        <Drawer.Address
          label="common.return"
          value={shipmentTemplateWithTags?.template?.data?.address_return}
          key="return"
        />,
        <Drawer.Order
          label="common.orderDetails"
          value={shipmentTemplateWithTags?.template?.data?.contents?.goods}
          key="order"
        />,
        <Drawer.Dimension
          label="common.dimensions"
          value={shipmentTemplateWithTags?.template?.data?.dimensions}
          key="dimensions"
        />,
        <Drawer.Plain label="common.weight" value={shipmentTemplateWithTags?.template?.data?.weight} key="weight" />,
        <Drawer.SubRow label="common.tags" value={shipmentTemplateWithTags?.tags?.map(tag => tag.name!)} key="tags" />,
        <Drawer.CarrierProduct
          label="common.carrierProduct"
          value={shipmentTemplateWithTags?.template?.data?.shipping_method}
          key="carrier"
        />,
        <Drawer.Plain
          label="shipmentTemplates.list.courierInstructions"
          value={shipmentTemplateWithTags?.template?.data?.courier_instructions}
          key="instructions"
        />,
      ]}
    />
  );
}
